/**
 * video-pipeline.js — Per-label video transform and fan-out stage.
 *
 * One pipeline is attached to a source label (e.g. 'camera'). It reads that label's video
 * track and fans it out to N independently-sized, independently-paced OUTPUT TAPS, each of
 * which is a canvas + its own MediaStreamTrack. A tap is what `publishStream({from})` hands
 * to the SFU — typically a small thumbnail for a mosaic tile.
 *
 * The design point is ONE read, N outputs. Publishing a 64x64 tile derived from the camera
 * must not cost a second capture or a second processing pass; it is a downscale blit off a
 * frame the pipeline already has.
 *
 * ── Modes ──────────────────────────────────────────────────────────────────────────────
 *   'tap-only'  (this phase) — the source label keeps publishing its NATIVE track, untouched.
 *                Only the taps go through a canvas. This matters: routing the camera through
 *                a canvas would replace a native camera track with a canvas track, and the
 *                browser could no longer ask the CAMERA to drop capture resolution under
 *                encoder pressure.
 *   'transform' (phase 2)   — a processor renders each frame; the source label publishes the
 *                processed canvas track and taps blit from the processed output.
 *
 * Consequence worth stating plainly: adding or removing a tap NEVER touches the source
 * label's published track — no replaceTrack, no renegotiation. Only installing or removing
 * a processor (phase 2) does.
 *
 * ── Frame drivers, in preference order ─────────────────────────────────────────────────
 *   1. MediaStreamTrackProcessor (Chrome/Edge) — a ReadableStream of VideoFrames off the
 *      track. NOT tied to the rendering steps, so it keeps delivering in a BACKGROUND TAB.
 *      This is the one that matters: rAF, rVFC and setTimeout are all throttled to ~1 fps in
 *      a hidden tab, which is exactly the freeze already observed with the mosaic bot.
 *   2. video.requestVideoFrameCallback — fires per decoded frame, no drift.
 *   3. setTimeout at the fastest tap's rate.
 *
 * ── Input loss ─────────────────────────────────────────────────────────────────────────
 * A tap OUTLIVES temporary loss of its source (camera turned off): the loop keeps running
 * and paints black, so the published stream stays alive and the remote tile goes black
 * rather than disappearing. Re-pointing the input (camera back on) resumes automatically.
 * A tap dies only when its own upstream is unpublished or the source stream is killed
 * outright — see Streams.local_killStream.
 */

import { MakedoError, ErrorCodes } from './errors.js';

const STATS_INTERVAL_MS = 400;   // matches the cadence the rest of the SDK reports stats at
const DEFAULT_TAP_FPS = 15;

/**
 * Source-crop and destination rect for a given fit mode.
 *  cover   — crop the source to the destination aspect, centred. No bars, no squash. The
 *            right default for face tiles in a uniform grid.
 *  contain — fit the whole source inside the destination, letterboxed.
 */
function fitRect(sw, sh, dw, dh, fit) {
    if (fit === 'contain') {
        const s = Math.min(dw / sw, dh / sh);
        const w = Math.max(1, Math.round(sw * s));
        const h = Math.max(1, Math.round(sh * s));
        return { sx: 0, sy: 0, cw: sw, ch: sh,
                 dx: Math.round((dw - w) / 2), dy: Math.round((dh - h) / 2), dw: w, dh: h,
                 letterbox: (w !== dw || h !== dh) };
    }
    const s = Math.max(dw / sw, dh / sh);
    const cw = Math.max(1, Math.min(sw, Math.round(dw / s)));
    const ch = Math.max(1, Math.min(sh, Math.round(dh / s)));
    return { sx: Math.round((sw - cw) / 2), sy: Math.round((sh - ch) / 2), cw, ch,
             dx: 0, dy: 0, dw, dh, letterbox: false };
}

class VideoPipeline {

    #onTrackEnded = null;

    /**
     * @param {Object} opts
     * @param {string} opts.sourceLabel   label this pipeline reads from ('camera', 'screenshare', ...)
     * @param {Function} [opts.onStats]   ({label, sourceFps, ms, driver, outputs}) every ~400ms
     * @param {Function} [opts.onError]   (MakedoError|Error) non-fatal per-frame failures
     * @param {Function} [opts.onSourceEnded] (label) the input track ended externally
     */
    constructor({ sourceLabel, onStats, onError, onSourceEnded } = {}) {
        this.sourceLabel = sourceLabel || '';
        this.onStats = onStats || null;
        this.onError = onError || null;
        this.onSourceEnded = onSourceEnded || null;

        this.mode = 'tap-only';
        this.destroyed = false;

        // Reserved for phase 2. Present but unused: the analysis buffer a processor renders
        // into is a THIRD resolution, independent of both the source and any tap size.
        this.analysisScale = 1;
        this.processor = null;

        this.outputs = new Map();       // name -> tap
        this.targetFps = DEFAULT_TAP_FPS;

        this.inputTrack = null;
        this.videoEl = null;            // only created for the rVFC / setTimeout drivers
        this.reader = null;             // only for the MediaStreamTrackProcessor driver
        this.readerGeneration = 0;
        this.loopHandle = null;
        this.driver = 'idle';

        this.frame = 0;
        this.busyMs = 0;
        this.lastStatsAt = 0;
        this.lastStatsFrame = 0;

        // Decided once: whether we can read frames off the track directly. When we can, we skip
        // the <video> element entirely — one less sink presenting frames we only want to copy.
        // (For a camera source that is presentation cost, not decode cost; the frames are raw.)
        this.useTrackProcessor = (typeof MediaStreamTrackProcessor !== 'undefined');
    }

    // ─────────────────────────────  INPUT  ─────────────────────────────

    /** Point the pipeline at a (new) source video track. Safe to call repeatedly. */
    setInputTrack(track) {
        if (this.destroyed) return;
        if (track === this.inputTrack) return;
        this.#detachInput();
        this.inputTrack = track || null;
        if (track) {
            this.#onTrackEnded = () => {
                console.debug(`VideoPipeline[${this.sourceLabel}]: source track ended`);
                this.#handleInputLost();
            };
            track.addEventListener('ended', this.#onTrackEnded);
            if (this.useTrackProcessor) { this.#attachTrackProcessor(track); }
            else { this.#attachVideoElement(track); }
        }
        this.#ensureLoop();
    }

    #detachInput() {
        const t = this.inputTrack;
        if (t && this.#onTrackEnded) { t.removeEventListener('ended', this.#onTrackEnded); }
        this.#onTrackEnded = null;
        this.inputTrack = null;

        // Invalidate any in-flight reader pump before dropping the reference.
        this.readerGeneration++;
        if (this.reader) {
            try { this.reader.cancel(); } catch (e) { /* already closed */ }
            this.reader = null;
        }
        if (this.videoEl) {
            try { this.videoEl.pause(); } catch (e) { /* ignore */ }
            this.videoEl.srcObject = null;
            this.videoEl = null;
        }
    }

    #handleInputLost() {
        this.#detachInput();
        this.driver = 'idle';
        this.#ensureLoop();     // keeps taps alive, painting black
        try { this.onSourceEnded?.(this.sourceLabel); } catch (e) { this.#reportError(e); }
    }

    #attachTrackProcessor(track) {
        let proc;
        try { proc = new MediaStreamTrackProcessor({ track }); }
        catch (e) {
            // Some track types are not processable; fall back rather than losing the tap.
            console.debug(`VideoPipeline[${this.sourceLabel}]: MediaStreamTrackProcessor unavailable, using <video>`);
            this.useTrackProcessor = false;
            this.#attachVideoElement(track);
            return;
        }
        const reader = proc.readable.getReader();
        this.reader = reader;
        const gen = ++this.readerGeneration;
        this.driver = 'track-processor';

        const pump = async () => {
            while (!this.destroyed && gen === this.readerGeneration) {
                let result;
                try { result = await reader.read(); }
                catch (e) { break; }
                if (result.done) break;
                const frame = result.value;
                try { this.#tick(frame, frame.displayWidth, frame.displayHeight); }
                catch (e) { this.#reportError(e); }
                finally { frame.close(); }
            }
            try { reader.releaseLock(); } catch (e) { /* ignore */ }
            // Only treat this as input loss if we are still the current reader — a
            // setInputTrack() swap bumps the generation and lands here harmlessly.
            if (!this.destroyed && gen === this.readerGeneration) { this.#handleInputLost(); }
        };
        pump();
    }

    #attachVideoElement(track) {
        const v = document.createElement('video');
        v.muted = true;
        v.playsInline = true;
        v.autoplay = true;
        v.srcObject = new MediaStream([track]);
        this.videoEl = v;
        this.driver = (typeof v.requestVideoFrameCallback === 'function') ? 'rvfc' : 'timer';
        const p = v.play();
        if (p && typeof p.catch === 'function') { p.catch(() => { /* autoplay of a muted local track */ }); }
    }

    // ─────────────────────────────  OUTPUTS  ─────────────────────────────

    /**
     * Add an output tap. Returns the MediaStreamTrack to publish.
     * @param {string} name              tap name (the publishing label)
     * @param {Object} spec
     * @param {number} spec.width        output width in pixels
     * @param {number} spec.height       output height in pixels
     * @param {number} [spec.fps=15]     tap frame rate, gated independently of the source
     * @param {string} [spec.fit='cover'] 'cover' | 'contain'
     */
    addOutput(name, { width, height, fps = DEFAULT_TAP_FPS, fit = 'cover' } = {}) {
        if (this.destroyed) {
            throw new MakedoError(ErrorCodes.PIPELINE_SOURCE_UNAVAILABLE, 'addOutput: pipeline has been destroyed', { context: 'VideoPipeline.addOutput', retriable: false });
        }
        if (!width || !height || width < 2 || height < 2) {
            throw new MakedoError(ErrorCodes.INVALID_PARAM, 'addOutput: width and height (>= 2) are required', { context: 'VideoPipeline.addOutput', retriable: false });
        }
        if (this.outputs.has(name)) {
            throw new MakedoError(ErrorCodes.INVALID_PARAM, `addOutput: tap "${name}" already exists on source "${this.sourceLabel}"`, { context: 'VideoPipeline.addOutput', retriable: false });
        }

        const canvas = document.createElement('canvas');
        canvas.width = Math.round(width);
        canvas.height = Math.round(height);
        if (typeof canvas.captureStream !== 'function') {
            throw new MakedoError(ErrorCodes.PIPELINE_UNSUPPORTED, 'This browser does not support canvas.captureStream()', { context: 'VideoPipeline.addOutput', retriable: false });
        }
        // No willReadFrequently: this canvas is written and captured, never read back.
        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // captureStream(0) + requestFrame() gives exact per-tap pacing. Where requestFrame is
        // missing, fall back to letting the browser sample the canvas at a fixed rate.
        let stream = canvas.captureStream(0);
        let track = stream.getVideoTracks()[0];
        const manual = !!(track && typeof track.requestFrame === 'function');
        if (!manual) {
            stream.getTracks().forEach(t => t.stop());
            stream = canvas.captureStream(fps);
            track = stream.getVideoTracks()[0];
        }
        if (!track) {
            throw new MakedoError(ErrorCodes.PIPELINE_UNSUPPORTED, 'canvas.captureStream() produced no video track', { context: 'VideoPipeline.addOutput', retriable: false });
        }
        // Favour frame rate over spatial detail — a 64x64 tile has no detail to preserve, and
        // 'detail' measurably costs frame rate on the grid.
        if ('contentHint' in track) { track.contentHint = 'motion'; }

        const tap = {
            name, canvas, ctx, stream, track,
            fps: Math.max(1, fps), fit,
            requestFrame: manual ? () => { try { track.requestFrame(); } catch (e) { /* track ended */ } } : null,
            reduce: null,           // lazily-created progressive-downscale scratch
            frames: 0,
            lastFrameAt: 0,
        };
        this.outputs.set(name, tap);
        this.#recomputeTargetFps();
        this.#ensureLoop();
        console.debug(`VideoPipeline[${this.sourceLabel}]: tap "${name}" ${canvas.width}x${canvas.height} @${tap.fps}fps fit=${fit}`);
        return track;
    }

    removeOutput(name) {
        const tap = this.outputs.get(name);
        if (!tap) return;
        this.outputs.delete(name);
        try { tap.track.stop(); } catch (e) { /* ignore */ }
        try { tap.stream.getTracks().forEach(t => t.stop()); } catch (e) { /* ignore */ }
        tap.reduce = null;
        this.#recomputeTargetFps();
        this.#ensureLoop();
    }

    hasOutputs() { return this.outputs.size > 0; }

    outputNames() { return Array.from(this.outputs.keys()); }

    // ─────────────────────────────  PHASE 2 SURFACE  ─────────────────────────────
    // Present and throwing so the four-state table is explicit rather than implied.

    setProcessor() {
        throw new MakedoError(ErrorCodes.PROCESSOR_UNSUPPORTED, 'Video processors are not available in this SDK version — only tap-only fan-out (publishStream({from})) is supported', { context: 'VideoPipeline.setProcessor', retriable: false });
    }

    /** Reserved for phase 2: scale of the analysis buffer a processor renders into. */
    setAnalysisScale(scale) { this.analysisScale = Math.max(0.05, Math.min(1, scale || 1)); }

    // ─────────────────────────────  LOOP  ─────────────────────────────

    #recomputeTargetFps() {
        let max = 0;
        for (const tap of this.outputs.values()) { if (tap.fps > max) max = tap.fps; }
        this.targetFps = max || DEFAULT_TAP_FPS;
    }

    #ensureLoop() {
        if (this.destroyed) return;
        if (this.outputs.size === 0) { this.#stopTimerLoop(); return; }
        // With a live track processor the reader pump already drives ticks.
        if (this.useTrackProcessor && this.inputTrack && this.reader) { this.#stopTimerLoop(); return; }
        if (!this.loopHandle) { this.#startTimerLoop(); }
    }

    #startTimerLoop() {
        const step = () => {
            this.loopHandle = null;
            if (this.destroyed || this.outputs.size === 0) return;
            const v = this.videoEl;
            const ready = !!(v && v.readyState >= 2 && v.videoWidth > 0);
            try { this.#tick(ready ? v : null, ready ? v.videoWidth : 0, ready ? v.videoHeight : 0); }
            catch (e) { this.#reportError(e); }
            this.#schedule(step);
        };
        this.#schedule(step);
    }

    #schedule(step) {
        const v = this.videoEl;
        if (v && typeof v.requestVideoFrameCallback === 'function' && v.readyState >= 2) {
            this.loopHandle = { kind: 'rvfc', el: v, id: v.requestVideoFrameCallback(() => step()) };
        } else {
            const ms = Math.max(4, Math.round(1000 / this.targetFps));
            this.loopHandle = { kind: 'timer', id: setTimeout(step, ms) };
        }
    }

    #stopTimerLoop() {
        const h = this.loopHandle;
        this.loopHandle = null;
        if (!h) return;
        if (h.kind === 'timer') { clearTimeout(h.id); }
        else if (h.kind === 'rvfc' && typeof h.el?.cancelVideoFrameCallback === 'function') {
            try { h.el.cancelVideoFrameCallback(h.id); } catch (e) { /* ignore */ }
        }
    }

    /** One pass over every tap. `source` is a VideoFrame, a <video>, or null (paint black). */
    #tick(source, sw, sh) {
        const now = performance.now();
        const t0 = now;
        this.frame++;

        for (const tap of this.outputs.values()) {
            const interval = 1000 / tap.fps;
            // 2ms tolerance. Without it a 30fps source feeding a 30fps tap systematically drops
            // every other frame whenever the source jitters a hair under the nominal interval.
            if (tap.lastFrameAt && (now - tap.lastFrameAt) < interval - 2) continue;
            try {
                if (source && sw > 0 && sh > 0) { this.#blit(tap, source, sw, sh); }
                else { tap.ctx.fillStyle = '#000'; tap.ctx.fillRect(0, 0, tap.canvas.width, tap.canvas.height); }
                tap.requestFrame?.();
                tap.frames++;
            } catch (e) {
                this.#reportError(e);
            }
            tap.lastFrameAt = now;
        }

        this.busyMs += performance.now() - t0;
        this.#maybeEmitStats(now);
    }

    #blit(tap, source, sw, sh) {
        const r = fitRect(sw, sh, tap.canvas.width, tap.canvas.height, tap.fit);
        let src = source, sx = r.sx, sy = r.sy, cw = r.cw, ch = r.ch;

        // Progressive halving. A single drawImage at a steep reduction (1280 -> 64 is 20:1)
        // aliases badly — Chrome samples bilinearly with no mip chain, so most source pixels
        // never contribute and the tile turns to noise. Halving until within 2:1 costs a few
        // sub-millisecond blits. In a mosaic the tile IS what everyone looks at, so this is
        // not a refinement.
        if (cw > r.dw * 2 || ch > r.dh * 2) {
            if (!tap.reduce) {
                const c = document.createElement('canvas');
                tap.reduce = { canvas: c, ctx: c.getContext('2d') };
            }
            const rd = tap.reduce;
            let rw = Math.max(r.dw, Math.round(cw / 2));
            let rh = Math.max(r.dh, Math.round(ch / 2));
            // Size once per source geometry — assigning width/height clears the canvas, so it
            // must not happen between the halving steps below.
            if (rd.canvas.width !== rw || rd.canvas.height !== rh) {
                rd.canvas.width = rw;
                rd.canvas.height = rh;
                rd.ctx.imageSmoothingEnabled = true;
                rd.ctx.imageSmoothingQuality = 'high';
            }
            rd.ctx.drawImage(src, sx, sy, cw, ch, 0, 0, rw, rh);
            while (rw > r.dw * 2 || rh > r.dh * 2) {
                const nw = Math.max(r.dw, Math.round(rw / 2));
                const nh = Math.max(r.dh, Math.round(rh / 2));
                rd.ctx.drawImage(rd.canvas, 0, 0, rw, rh, 0, 0, nw, nh);
                rw = nw; rh = nh;
            }
            src = rd.canvas; sx = 0; sy = 0; cw = rw; ch = rh;
        }

        if (r.letterbox) {
            tap.ctx.fillStyle = '#000';
            tap.ctx.fillRect(0, 0, tap.canvas.width, tap.canvas.height);
        }
        tap.ctx.drawImage(src, sx, sy, cw, ch, r.dx, r.dy, r.dw, r.dh);
    }

    #maybeEmitStats(now) {
        if (!this.onStats) return;
        if (!this.lastStatsAt) { this.lastStatsAt = now; this.lastStatsFrame = this.frame; return; }
        const elapsed = now - this.lastStatsAt;
        if (elapsed < STATS_INTERVAL_MS) return;

        const outputs = {};
        for (const tap of this.outputs.values()) {
            outputs[tap.name] = {
                fps: +(tap.frames * 1000 / elapsed).toFixed(1),
                width: tap.canvas.width,
                height: tap.canvas.height,
            };
            tap.frames = 0;
        }
        const ticks = this.frame - this.lastStatsFrame;
        const payload = {
            label: this.sourceLabel,
            driver: this.driver,
            sourceFps: +(ticks * 1000 / elapsed).toFixed(1),
            ms: ticks ? +(this.busyMs / ticks).toFixed(2) : 0,
            outputs,
        };
        this.busyMs = 0;
        this.lastStatsFrame = this.frame;
        this.lastStatsAt = now;
        try { this.onStats(payload); } catch (e) { this.#reportError(e); }
    }

    #reportError(e) {
        if (this.onError) { try { this.onError(e); return; } catch (_) { /* fall through */ } }
        console.warn(`VideoPipeline[${this.sourceLabel}]:`, e);
    }

    // ─────────────────────────────  TEARDOWN  ─────────────────────────────

    /** Stop everything and release canvases. Does NOT stop the input track — Streams owns it. */
    destroy() {
        if (this.destroyed) return;
        this.destroyed = true;
        this.#stopTimerLoop();
        this.#detachInput();
        for (const name of Array.from(this.outputs.keys())) { this.removeOutput(name); }
        this.outputs.clear();
        this.onStats = null;
        this.onError = null;
        this.onSourceEnded = null;
    }
}

export default VideoPipeline;
